import { useRef, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDrag } from '@use-gesture/react';

// Swipe-to-commit (edit/delete) + long-press peek, wrapping any row.
//
// Deliberately simpler than the first version of this component: that one
// had a "swipe reveals a small action button, then a *separate* precise tap
// on it commits" flow. In practice that second tap proved unreliable on
// real devices (small moving target, easy to mis-hit), so this version
// removes the intermediate state entirely — a swipe that clears the
// threshold on release commits immediately, full stop. Anything short of
// that just springs back to 0, no lingering half-open state to interact
// with. leftAction/rightAction are still shown as a visual hint *during*
// the drag (so it's clear what letting go will do), just no longer a
// separate tappable target of their own.
//
// apple-design skill compliance notes, since this is the one place in the
// app doing real gesture-driven motion:
//   - 1:1 tracking: the row's translateX is written directly to the DOM via
//     a ref on every drag frame (not React state) — no re-render lag, no
//     animating a layout property, purely a compositor transform.
//   - Interruptibility: the settle animation always starts from whatever
//     xRef.current currently is (the live on-screen position), never from a
//     logical target.
//   - Velocity handoff: release velocity feeds the settle spring's initial
//     velocity directly, so there's no seam between dragging and animating.
//   - Disambiguation: long-press timer and swipe tracking both start on
//     pointerdown; whichever crosses its threshold first (10px movement vs
//     the timer) wins and cancels the other.
//   - touch-action: pan-y so vertical scroll stays native until a
//     horizontal swipe is actually confirmed.
const REVEAL_WIDTH = 72;
const COMMIT_DISTANCE = REVEAL_WIDTH * 0.85; // reachable without fighting rubberband resistance much
const FLICK_VELOCITY = 0.5;
const FLICK_MIN_DISTANCE = REVEAL_WIDTH * 0.35;
const DIRECTION_THRESHOLD = 10;
const LONG_PRESS_MS = 350;

export const isTouchDevice =
  typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);

// Small hand-rolled damped-spring integrator (critically damped at
// damping=1, underdamped/bouncy below 1) — the only piece @use-gesture
// doesn't supply itself. damping 1.0/response 0.35 for the spring-back to
// 0; damping 0.8 is available for a momentum-driven settle but this
// version mostly either commits instantly or springs back to 0.
function animateSpring({ from, to, velocity = 0, damping = 1, response = 0.35, onUpdate, onComplete }) {
  const stiffness = (2 * Math.PI / response) ** 2;
  const dampingCoef = damping * 2 * Math.sqrt(stiffness);
  let value = from;
  let v = velocity;
  let lastTime = performance.now();
  let raf;
  const step = (now) => {
    const dt = Math.min((now - lastTime) / 1000, 1 / 30);
    lastTime = now;
    const acceleration = -stiffness * (value - to) - dampingCoef * v;
    v += acceleration * dt;
    value += v * dt;
    onUpdate(value);
    if (Math.abs(value - to) < 0.5 && Math.abs(v) < 20) {
      onUpdate(to);
      onComplete?.();
    } else {
      raf = requestAnimationFrame(step);
    }
  };
  raf = requestAnimationFrame(step);
  return () => cancelAnimationFrame(raf);
}

// leftAction/rightAction: { icon: LucideIcon, bg: 'bg-violet-500', onCommit }
//   left = committed by swiping right; right = committed by swiping left.
// peekContent: optional ReactNode shown on long-press only — never by
//   swiping, in any row that uses this component (also rendered inside the
//   peek popup alongside Editar/Eliminar buttons wired to leftAction/
//   rightAction, if those are configured, as a second way to reach them).
// disabled: pure passthrough (desktop/mouse — existing hover-visible icon
// buttons in the row's own children keep working exactly as before).
// swipeDisabled: keeps long-press-to-peek (and leftAction/rightAction as
// buttons inside that peek) fully working, but the swipe-to-drag gesture
// itself never engages and its colored hint panels never render — for
// layouts (e.g. a grid of tiles) where a horizontal swipe doesn't make
// sense but a long-press action menu still does.
export default function SwipeableRow({ children, leftAction, rightAction, peekContent, disabled, swipeDisabled, testId }) {
  const rowRef = useRef(null);
  const xRef = useRef(0);
  const cancelSpringRef = useRef(null);
  const longPressTimer = useRef(null);
  const gestureIntent = useRef(null); // 'swipe' | 'peek' | null

  const [previewSide, setPreviewSide] = useState(null); // visual hint only, while actively dragging
  const [peeking, setPeeking] = useState(false);

  const setX = useCallback((val) => {
    xRef.current = val;
    if (rowRef.current) rowRef.current.style.transform = `translateX(${val}px)`;
  }, []);

  const springBack = useCallback((velocity) => {
    cancelSpringRef.current?.();
    cancelSpringRef.current = animateSpring({
      from: xRef.current,
      to: 0,
      velocity: velocity || 0,
      damping: 1.0,
      response: 0.35,
      onUpdate: setX,
    });
  }, [setX]);

  const clearLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };
  useEffect(() => clearLongPress, []);

  // Long-press is detected via plain native touch events, deliberately
  // *not* through useDrag's own gesture callback below. A motionless hold
  // is, by @use-gesture's own definition, indistinguishable from a "tap"
  // until movement either does or doesn't happen — its `tap` flag is about
  // distance moved, not time held — so routing long-press through the same
  // callback meant a still hold could get classified/filtered as a tap
  // before the timer ever got a fair chance to fire. onTouchStart/Move/End
  // are separate React props from the onPointerDown/etc. that bind() below
  // returns, so both sets of handlers coexist on the same element with no
  // collision — this one only ever touches gestureIntent/the peek timer,
  // never the swipe/translateX logic.
  const touchStart = useRef(null);
  const handleTouchStart = (e) => {
    if (disabled || !peekContent || e.touches.length !== 1) return;
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
    gestureIntent.current = null;
    clearLongPress();
    longPressTimer.current = setTimeout(() => {
      if (gestureIntent.current === null) {
        gestureIntent.current = 'peek';
        setPeeking(true);
      }
    }, LONG_PRESS_MS);
  };
  const handleTouchMove = (e) => {
    if (!touchStart.current || !longPressTimer.current) return;
    const t = e.touches[0];
    if (!t) return;
    if (Math.abs(t.clientX - touchStart.current.x) > DIRECTION_THRESHOLD ||
        Math.abs(t.clientY - touchStart.current.y) > DIRECTION_THRESHOLD) {
      clearLongPress(); // real movement now — becoming a swipe/scroll, not a hold
    }
  };
  const handleTouchEnd = () => {
    touchStart.current = null;
    clearLongPress();
  };

  const bind = useDrag(({ movement: [mx], velocity: [vx], last, tap, event }) => {
    if (disabled) return;
    if (tap) return;

    // Swipe is only a real, competing gesture in a direction that actually
    // has an action configured — e.g. TransactionList's "Actividad
    // reciente" preview rows pass no leftAction/rightAction at all (peek is
    // its *only* interaction there). Without this check, harmless finger
    // jitter during an attempted long-press would still flip gestureIntent
    // to 'swipe' and cancel the peek timer, even though nothing would have
    // happened anyway — leaving that hold dead with no peek and no swipe.
    const hasSwipeTarget = (mx > 0 && leftAction) || (mx < 0 && rightAction);
    if (gestureIntent.current === null && hasSwipeTarget && Math.abs(mx) > DIRECTION_THRESHOLD) {
      gestureIntent.current = 'swipe';
      clearLongPress();
    }

    // Rightward drags can otherwise get contested by the browser's own
    // "swipe right = go back" navigation gesture — passive:false (below)
    // only makes this *legal* to call, it doesn't call it on its own.
    if (gestureIntent.current === 'swipe' && event?.cancelable) {
      event.preventDefault();
    }

    if (gestureIntent.current === 'peek') {
      if (last) { setPeeking(false); gestureIntent.current = null; }
      return;
    }

    if (gestureIntent.current !== 'swipe') {
      if (last) clearLongPress();
      return;
    }

    setX(mx);
    setPreviewSide(mx > 0 ? 'left' : mx < 0 ? 'right' : null);

    if (last) {
      clearLongPress();
      // Swipe always commits its direct action once cleared — left
      // deletes, right opens the edit modal. Peek (the detail/preview
      // popup) is reached only by long-press, never by swiping, in every
      // row that uses this component.
      const cleared = Math.abs(mx) > COMMIT_DISTANCE || (Math.abs(vx) > FLICK_VELOCITY && Math.abs(mx) > FLICK_MIN_DISTANCE);
      setPreviewSide(null);
      if (cleared) {
        // Instant reset, not an animated spring-back — the row is about to
        // unmount (delete) or get covered by a dialog (edit) either way.
        setX(0);
        if (mx > 0) leftAction?.onCommit?.();
        else rightAction?.onCommit?.();
      } else {
        // Animate back from wherever it actually is (xRef.current, read
        // inside springBack) — setting x to 0 first here would make the
        // "spring" animate from 0 to 0, i.e. not move at all.
        springBack(vx * 800);
      }
      gestureIntent.current = null;
    }
  }, {
    axis: 'x',
    bounds: { left: -REVEAL_WIDTH, right: REVEAL_WIDTH },
    rubberband: 0.4,
    filterTaps: true,
    eventOptions: { passive: false },
    enabled: !disabled && !swipeDisabled && !!(leftAction || rightAction || peekContent),
  });

  if (disabled || (!leftAction && !rightAction && !peekContent)) {
    return <div data-testid={testId}>{children}</div>;
  }

  return (
    <div className="relative overflow-hidden rounded-xl" data-testid={testId}>
      {leftAction && !swipeDisabled && (
        <div
          className={`absolute inset-y-0 left-0 flex items-center justify-center text-white transition-opacity duration-150 ${leftAction.bg} ${previewSide === 'left' ? 'opacity-100' : 'opacity-60'}`}
          style={{ width: REVEAL_WIDTH }}
        >
          <leftAction.icon className="w-5 h-5" />
        </div>
      )}
      {rightAction && !swipeDisabled && (
        <div
          className={`absolute inset-y-0 right-0 flex items-center justify-center text-white transition-opacity duration-150 ${rightAction.bg} ${previewSide === 'right' ? 'opacity-100' : 'opacity-60'}`}
          style={{ width: REVEAL_WIDTH }}
        >
          <rightAction.icon className="w-5 h-5" />
        </div>
      )}
      {/* bg-slate-950 here is load-bearing, not decorative: the row content
          (children) is usually a .glass-card at ~70% opacity, so without an
          opaque backdrop directly behind it, the action colors bled through
          at all times, even at rest. */}
      <div
        ref={rowRef}
        {...bind()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        onContextMenu={(e) => e.preventDefault()}
        style={{
          touchAction: 'pan-y',
          overscrollBehaviorX: 'none',
          willChange: 'transform',
          position: 'relative',
          // Suppresses the native "hold to select text / open callout menu"
          // gesture that otherwise competes with (and can outright win
          // against) the long-press timer above on real mobile browsers.
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          userSelect: 'none',
        }}
        className="bg-slate-950 rounded-xl"
      >
        {children}
      </div>

      {peeking && peekContent && createPortal(
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-black/50 animate-modal-in"
          data-testid="row-peek-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setPeeking(false); }}
        >
          <div className="w-full max-w-sm glass-card rounded-2xl p-5 shadow-2xl shadow-black/50 noise-overlay">
            {peekContent}
            {(leftAction || rightAction) && (
              <div className="flex gap-2 mt-5">
                {leftAction && (
                  <button
                    type="button"
                    data-testid="row-peek-left-action"
                    onClick={() => { setPeeking(false); leftAction.onCommit?.(); }}
                    className={`flex-1 flex items-center justify-center gap-2 h-11 rounded-xl text-sm font-semibold text-white transition-transform active:scale-[0.97] ${leftAction.bg}`}
                  >
                    <leftAction.icon className="w-4 h-4" />
                    {leftAction.label || 'Editar'}
                  </button>
                )}
                {rightAction && (
                  <button
                    type="button"
                    data-testid="row-peek-right-action"
                    onClick={() => { setPeeking(false); rightAction.onCommit?.(); }}
                    className={`flex-1 flex items-center justify-center gap-2 h-11 rounded-xl text-sm font-semibold text-white transition-transform active:scale-[0.97] ${rightAction.bg}`}
                  >
                    <rightAction.icon className="w-4 h-4" />
                    {rightAction.label || 'Eliminar'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
